/**
 * Worlds Configuration
 *
 * Each world is a virtual tour experience. Users browse a grid of worlds,
 * then enter one to explore 360° panoramas.
 *
 * To add a new world:
 * 1. Add equirectangular JPGs to public/tour/<world-id>/
 * 2. Add a world entry below with nodes and links
 * 3. The card thumbnail should be a regular photo in public/tour/<world-id>/thumbnail.jpg
 */

export interface WorldNode {
  id: string
  name: string
  panorama: string
  links: { nodeId: string; position: { yaw: number; pitch: number } }[]
}

export interface World {
  id: string
  title: string
  location: string
  description: string
  thumbnail: string
  isLocked: boolean
  isNew: boolean
  startNodeId: string
  nodes: WorldNode[]
}

export const WORLDS: World[] = [
  {
    id: 'hotel-suite',
    title: 'Austin Hotel',
    location: 'Austin, Texas',
    description: 'A stunning boutique hotel with exposed brick, artisan tile, and vintage charm.',
    thumbnail: '/tour/thumbnail.jpg',
    isLocked: false,
    isNew: true,
    startNodeId: 'hallway-doors',
    nodes: [
      {
        id: 'hallway-doors',
        name: 'Hallway',
        panorama: '/tour/hallway-doors.jpg',
        links: [
          { nodeId: 'staircase-landing', position: { yaw: -1.2, pitch: -0.15 } },
          { nodeId: 'bathroom-sinks', position: { yaw: 1.8, pitch: -0.1 } },
          { nodeId: 'bedroom-entrance', position: { yaw: 0.3, pitch: -0.1 } },
        ],
      },
      {
        id: 'staircase-landing',
        name: 'Staircase Landing',
        panorama: '/tour/staircase-landing.jpg',
        links: [
          { nodeId: 'hallway-doors', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'staircase-window', position: { yaw: -0.5, pitch: -0.2 } },
        ],
      },
      {
        id: 'staircase-window',
        name: 'Staircase',
        panorama: '/tour/staircase-window.jpg',
        links: [
          { nodeId: 'staircase-landing', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },
      {
        id: 'bathroom-sinks',
        name: 'Bathroom - Vanity',
        panorama: '/tour/bathroom-sinks.jpg',
        links: [
          { nodeId: 'hallway-doors', position: { yaw: 2.5, pitch: -0.1 } },
          { nodeId: 'bathroom-sinks-2', position: { yaw: 0.8, pitch: -0.1 } },
          { nodeId: 'bathroom-tub', position: { yaw: -0.8, pitch: -0.1 } },
        ],
      },
      {
        id: 'bathroom-sinks-2',
        name: 'Bathroom - Mirror',
        panorama: '/tour/bathroom-sinks-2.jpg',
        links: [
          { nodeId: 'bathroom-sinks', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'bathroom-shower', position: { yaw: 0.5, pitch: -0.1 } },
        ],
      },
      {
        id: 'bathroom-tub',
        name: 'Bathroom - Tub',
        panorama: '/tour/bathroom-tub.jpg',
        links: [
          { nodeId: 'bathroom-sinks', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'bathroom-shower', position: { yaw: -1.0, pitch: -0.1 } },
        ],
      },
      {
        id: 'bathroom-shower',
        name: 'Bathroom - Shower',
        panorama: '/tour/bathroom-shower.jpg',
        links: [
          { nodeId: 'bathroom-tub', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'bathroom-sinks-2', position: { yaw: -1.5, pitch: -0.1 } },
        ],
      },

      // === BEDROOM ===
      {
        id: 'bedroom-entrance',
        name: 'Bedroom Entrance',
        panorama: '/tour/bedroom-entrance.jpg',
        links: [
          { nodeId: 'hallway-doors', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'bedroom', position: { yaw: -0.8, pitch: -0.1 } },
          { nodeId: 'bathroom-sinks', position: { yaw: 1.2, pitch: -0.1 } },
        ],
      },
      {
        id: 'bedroom',
        name: 'Bedroom',
        panorama: '/tour/bedroom.jpg',
        links: [
          { nodeId: 'bedroom-entrance', position: { yaw: 2.2, pitch: -0.1 } },
          { nodeId: 'bedroom-lounge', position: { yaw: -1.0, pitch: -0.1 } },
          { nodeId: 'bedroom-closeup', position: { yaw: 0.5, pitch: -0.1 } },
          { nodeId: 'bedroom-bed', position: { yaw: 1.2, pitch: -0.1 } },
        ],
      },
      {
        id: 'bedroom-lounge',
        name: 'Lounge Area',
        panorama: '/tour/bedroom-lounge.jpg',
        links: [
          { nodeId: 'bedroom', position: { yaw: 1.5, pitch: -0.1 } },
          { nodeId: 'bedroom-entrance', position: { yaw: -0.3, pitch: -0.1 } },
        ],
      },
      {
        id: 'bedroom-closeup',
        name: 'Bed - Nightstand',
        panorama: '/tour/bedroom-closeup.jpg',
        links: [
          { nodeId: 'bedroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'bedroom-lounge', position: { yaw: 2.5, pitch: -0.1 } },
        ],
      },
      {
        id: 'bedroom-bed',
        name: 'Bedroom - Overview',
        panorama: '/tour/bedroom-bed.jpg',
        links: [
          { nodeId: 'bedroom', position: { yaw: -1.5, pitch: -0.1 } },
          { nodeId: 'bedroom-entrance', position: { yaw: 2.0, pitch: -0.1 } },
        ],
      },
    ],
  },

  // ============================================================
  // OLD WORLD ESTATE — Red Floral Suite
  // ============================================================
  // WORKFLOW: linear chain entry → hub at bedroom → hub at bathroom
  //
  //   Suite Door (START — entrance to building)
  //        ↓
  //   Entry Hall (hallway after entering)
  //        ↓
  //   Stairs (going up to the suite)
  //        ↓
  //   Main Room (bedroom — hub for all suite views)
  //        ├── Bed / Mirror / Sitting Area / Window Nook / Stair View
  //        └── Bathroom Doorway → Bathroom (hub) → Vanity/Tub/Shower/Toilet
  //
  // Every non-start room has a "back" arrow.
  // ============================================================
  {
    id: 'old-world-estate',
    title: 'Old World Estate',
    location: 'HIDDEN LOCATION',
    description: 'Layered fabrics, soft lighting, floral walls, worn wood floors, and quiet little corners that make the space feel personal instead of perfect. I welcome you into this comforting, romantic space that feels almost untouched by time. A place you accidentally discover and never really want to leave. This room made me feel like a time traveler.',
    thumbnail: '/tour/old-world-estate/thumbnail.jpg',
    isLocked: false,
    isNew: true,
    startNodeId: 'owe-suite-door',
    nodes: [
      // === STEP 1: SUITE DOOR (entrance to the building) ===
      {
        id: 'owe-suite-door',
        name: 'Suite Door',
        panorama: '/tour/old-world-estate/hall-door.jpg',
        links: [
          // Forward into the building — flipped 180°
          { nodeId: 'owe-entry-hall', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },

      // === STEP 2: ENTRY HALL (the hallway inside) ===
      {
        id: 'owe-entry-hall',
        name: 'Entry Hall',
        panorama: '/tour/old-world-estate/hall-window.jpg',
        links: [
          { nodeId: 'owe-suite-door', position: { yaw: 0, pitch: -0.1 } },
          // Forward to the stairs — flipped 180°
          { nodeId: 'owe-stairs', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },

      // === STEP 3: STAIR LANDING — top of stairs, inside the suite ===
      // (stair-landing and bedroom are the same physical room, different angles)
      {
        id: 'owe-stairs',
        name: 'Stair Landing',
        panorama: '/tour/old-world-estate/stair-landing.jpg',
        links: [
          // Back down the stairs to the entry hall — flipped 180°
          { nodeId: 'owe-entry-hall', position: { yaw: 0, pitch: -0.1 } },
          // Look into the main room — flipped 180°
          { nodeId: 'owe-bedroom', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },

      // === STEP 4: BEDROOM (main room view) ===
      // Hub with only 3 forward links: bed, sitting area, bathroom
      // Plus back to stairs (the way you came in)
      {
        id: 'owe-bedroom',
        name: 'Main Room',
        panorama: '/tour/old-world-estate/bedroom-main.jpg',
        links: [
          // Back to where you came from (the stair landing — same room, different angle)
          { nodeId: 'owe-stairs', position: { yaw: -1.5, pitch: -0.1 } },
          // Forward — the bed
          { nodeId: 'owe-bed', position: { yaw: 0, pitch: -0.1 } },
          // Right — the sitting/window area
          { nodeId: 'owe-sitting', position: { yaw: 1.2, pitch: -0.1 } },
          // Bathroom (the pink doorway)
          { nodeId: 'owe-bathroom-doorway', position: { yaw: 0.5, pitch: -0.1 } },
        ],
      },

      // Bed area: 2 close-up views chained
      {
        id: 'owe-bed',
        name: 'Bed',
        panorama: '/tour/old-world-estate/bedroom-bed.jpg',
        links: [
          { nodeId: 'owe-bedroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-bed-mirror', position: { yaw: 0, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bed-mirror',
        name: 'Bedside',
        panorama: '/tour/old-world-estate/bedroom-mirror.jpg',
        links: [
          { nodeId: 'owe-bed', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },

      // Sitting / Window area: chained together
      {
        id: 'owe-sitting',
        name: 'Sitting Area',
        panorama: '/tour/old-world-estate/sitting-area.jpg',
        links: [
          { nodeId: 'owe-bedroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-window-nook', position: { yaw: 0, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-window-nook',
        name: 'Window Nook',
        panorama: '/tour/old-world-estate/window-nook.jpg',
        links: [
          { nodeId: 'owe-sitting', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-stair-view', position: { yaw: 0, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-stair-view',
        name: 'Reading Nook',
        panorama: '/tour/old-world-estate/stair-view.jpg',
        links: [
          { nodeId: 'owe-window-nook', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },

      // === BATHROOM (separate hub) ===
      {
        id: 'owe-bathroom-doorway',
        name: 'Bathroom Doorway',
        panorama: '/tour/old-world-estate/bathroom-doorway.jpg',
        links: [
          { nodeId: 'owe-bedroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-bathroom', position: { yaw: 0, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bathroom',
        name: 'Bathroom',
        panorama: '/tour/old-world-estate/bathroom-main.jpg',
        links: [
          { nodeId: 'owe-bathroom-doorway', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-bathroom-vanity', position: { yaw: 0.7, pitch: -0.1 } },
          { nodeId: 'owe-bathroom-tub', position: { yaw: 0, pitch: -0.1 } },
          { nodeId: 'owe-bathroom-shower', position: { yaw: -0.7, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bathroom-vanity',
        name: 'Vanity',
        panorama: '/tour/old-world-estate/bathroom-vanity.jpg',
        links: [
          { nodeId: 'owe-bathroom', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bathroom-tub',
        name: 'Tub',
        panorama: '/tour/old-world-estate/bathroom-tub.jpg',
        links: [
          { nodeId: 'owe-bathroom', position: { yaw: Math.PI, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bathroom-shower',
        name: 'Shower',
        panorama: '/tour/old-world-estate/bathroom-shower.jpg',
        links: [
          { nodeId: 'owe-bathroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-bathroom-toilet', position: { yaw: 0.7, pitch: -0.1 } },
        ],
      },
      {
        id: 'owe-bathroom-toilet',
        name: 'Toilet',
        panorama: '/tour/old-world-estate/bathroom-toilet.jpg',
        links: [
          { nodeId: 'owe-bathroom', position: { yaw: Math.PI, pitch: -0.1 } },
          { nodeId: 'owe-bathroom-shower', position: { yaw: -0.7, pitch: -0.1 } },
        ],
      },
    ],
  },
]
